import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Save, RefreshCw } from 'lucide-react';

interface NotificationPreferences {
  job_updates: boolean;
  new_applications: boolean;
  application_status_changes: boolean;
  candidate_updates: boolean;
  company_updates: boolean;
  comment_mentions: boolean;
  new_documents: boolean;
  system_notifications: boolean;
  email_notifications: boolean;
  push_notifications: boolean;
}

const defaultPreferences: NotificationPreferences = {
  job_updates: true,
  new_applications: true,
  application_status_changes: true,
  candidate_updates: true,
  company_updates: true,
  comment_mentions: true,
  new_documents: true,
  system_notifications: true,
  email_notifications: true,
  push_notifications: true,
};

export const NotificationPreferences: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPreferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load preferences from profiles.settings
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('settings')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error loading notification preferences:', error);
          return;
        }

        if (data?.settings?.notifications) {
          setPreferences({
            ...defaultPreferences,
            ...data.settings.notifications,
          });
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user]);

  // Save preferences to profiles.settings
  const savePreferences = async () => {
    if (!user) return;

    setSaving(true);
    try {
      // First, get current settings
      const { data: currentData, error: fetchError } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      // Merge with existing settings
      const currentSettings = currentData?.settings || {};
      const updatedSettings = {
        ...currentSettings,
        notifications: preferences,
      };

      // Update the settings
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ settings: updatedSettings })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      toast({
        title: t('settings.notifications.saved', 'Preferences saved'),
        description: t('settings.notifications.savedDescription', 'Your notification preferences have been updated.'),
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: t('settings.notifications.error', 'Error'),
        description: t('settings.notifications.saveError', 'Failed to save preferences. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const resetToDefaults = () => {
    setPreferences(defaultPreferences);
  };

  // Handle preference change
  const handlePreferenceChange = (key: keyof NotificationPreferences, value: boolean) => {
    setPreferences(prev => {
      // If push notifications are being disabled, disable ALL notifications
      if (key === 'push_notifications' && !value) {
        return {
          ...prev,
          [key]: value,
          job_updates: false,
          new_applications: false,
          application_status_changes: false,
          candidate_updates: false,
          company_updates: false,
          comment_mentions: false,
          new_documents: false,
          system_notifications: false,
        };
      }
      
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin" />
            <span className="ml-2">{t('settings.notifications.loading', 'Loading preferences...')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.notifications.title', 'Notification Preferences')}</CardTitle>
          <CardDescription>
            {t('settings.notifications.description', 'Choose which notifications you want to receive and how you want to receive them.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Notification Types */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              {t('settings.notifications.types', 'Notification Types')}
            </h3>
            
            <div className="space-y-0">
              {[
                {
                  key: 'job_updates',
                  label: t('settings.notifications.jobUpdates', 'Job Updates'),
                  description: t('settings.notifications.jobUpdatesDesc', 'When jobs are created, updated, or status changes')
                },
                {
                  key: 'new_applications',
                  label: t('settings.notifications.newApplications', 'New Applications'),
                  description: t('settings.notifications.newApplicationsDesc', 'When new candidates apply to jobs')
                },
                {
                  key: 'application_status_changes',
                  label: t('settings.notifications.applicationStatus', 'Application Status Changes'),
                  description: t('settings.notifications.applicationStatusDesc', 'When application status changes (screening, interview, etc.)')
                },
                {
                  key: 'candidate_updates',
                  label: t('settings.notifications.candidateUpdates', 'Candidate Updates'),
                  description: t('settings.notifications.candidateUpdatesDesc', 'When candidate information is updated')
                },
                {
                  key: 'company_updates',
                  label: t('settings.notifications.companyUpdates', 'Company Updates'),
                  description: t('settings.notifications.companyUpdatesDesc', 'When company information is updated')
                },
                {
                  key: 'comment_mentions',
                  label: t('settings.notifications.commentMentions', 'Comment Mentions'),
                  description: t('settings.notifications.commentMentionsDesc', 'When you are mentioned in comments')
                },
                {
                  key: 'new_documents',
                  label: t('settings.notifications.newDocuments', 'Document Updates'),
                  description: t('settings.notifications.newDocumentsDesc', 'When new documents are added to candidates or jobs')
                },
                {
                  key: 'system_notifications',
                  label: t('settings.notifications.systemNotifications', 'System Notifications'),
                  description: t('settings.notifications.systemNotificationsDesc', 'Important system updates and announcements')
                }
              ].map((item, index) => (
                <div 
                  key={item.key}
                  className={`flex items-center justify-between p-3 rounded-sm ${
                    index % 2 === 0 ? 'bg-muted/60' : ''
                  }`}
                >
                  <div className="space-y-0.5">
                    <Label htmlFor={item.key}>
                      {item.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                  <Switch
                    id={item.key}
                    checked={preferences[item.key as keyof NotificationPreferences]}
                    disabled={!preferences.push_notifications}
                    onCheckedChange={(checked) => handlePreferenceChange(item.key as keyof NotificationPreferences, checked)}
                  />
                </div>
              ))}
            </div>
          </div>



          {/* Delivery Methods */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              {t('settings.notifications.delivery', 'Delivery Methods')}
            </h3>
            
            <div className="space-y-0">
              {[
                {
                  key: 'email_notifications',
                  label: t('settings.notifications.email', 'Email Notifications'),
                  description: t('settings.notifications.emailDesc', 'Receive notifications via email'),
                  disabled: true
                },
                {
                  key: 'push_notifications',
                  label: t('settings.notifications.push', 'Push Notifications'),
                  description: t('settings.notifications.pushDesc', 'Receive notifications in the browser'),
                  disabled: false
                }
              ].map((item, index) => (
                <div 
                  key={item.key}
                  className={`flex items-center justify-between p-3 rounded-sm ${
                    index % 2 === 0 ? 'bg-muted/60' : ''
                  } ${item.disabled ? 'opacity-60' : ''}`}
                >
                  <div className="space-y-0.5">
                    <Label htmlFor={item.key} className={item.disabled ? 'text-muted-foreground' : ''}>
                      {item.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                  {item.disabled ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <div className="cursor-not-allowed">
                          <Switch
                            id={item.key}
                            checked={false}
                            disabled={true}
                            className="opacity-50"
                          />
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-2">
                        <p className="text-sm font-medium">Coming Soon</p>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Switch
                      id={item.key}
                      checked={preferences[item.key as keyof NotificationPreferences]}
                      onCheckedChange={(checked) => handlePreferenceChange(item.key as keyof NotificationPreferences, checked)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={resetToDefaults}
              disabled={saving}
            >
              {t('settings.notifications.reset', 'Reset to Defaults')}
            </Button>
            
            <Button
              onClick={savePreferences}
              disabled={saving}
            >
              {saving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t('settings.notifications.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('settings.notifications.save', 'Save Preferences')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 